<template>
    <app-modal modal-id="app-stock-detail-modal" modal-size="large" modal-alignment="top" @close-modal="closeModal">
        <template slot="header">
            <h5 class="modal-heading">{{ detail.variant ? detail.variant.name : $t('Stock detail') }}</h5>
            <button class="close outline-none" @click.prevent="closeModal"><app-icon name="x" /></button>
        </template>

        <template slot="body">
            <div v-if="loading" class="text-center p-4 text-muted">Loading…</div>

            <div v-else>
                <!-- summary -->
                <div class="d-flex flex-wrap mb-3" v-if="detail.variant">
                    <div class="mr-4"><span class="text-muted">Category:</span> {{ categoryName }}</div>
                    <div class="mr-4"><span class="text-muted">Sub-category:</span> {{ detail.variant.product && detail.variant.product.category ? detail.variant.product.category.name : '-' }}</div>
                    <div class="mr-4"><span class="text-muted">Sub-sub-category:</span> {{ detail.variant.product && detail.variant.product.sub_category ? detail.variant.product.sub_category.name : '-' }}</div>
                    <div class="mr-4"><span class="text-muted">Unit:</span> {{ detail.variant.product && detail.variant.product.unit ? detail.variant.product.unit.name : '-' }}</div>
                </div>
                <div class="d-flex flex-wrap mb-3" v-if="detail.variant">
                    <div class="mr-4"><span class="text-muted">Total stock:</span> <strong :class="detail.total_qty <= 0 ? 'text-danger' : ''">{{ detail.total_qty }}</strong></div>
                    <div class="mr-4"><span class="text-muted">Reorder level:</span> {{ detail.variant.stock_reminder_quantity || 0 }}</div>
                    <div v-if="detail.variant.upc"><span class="text-muted">UPC:</span> #{{ detail.variant.upc }}</div>
                </div>

                <!-- stock by location -->
                <div class="font-weight-bold mb-1">Stock by location</div>
                <div class="table-responsive mb-4" style="max-height:200px;overflow:auto;">
                    <table class="table table-sm">
                        <thead><tr><th>Location</th><th>Type</th><th class="text-right">Quantity</th></tr></thead>
                        <tbody>
                            <tr v-for="(l, i) in detail.locations" :key="'l'+i">
                                <td>{{ l.location_name }}</td>
                                <td class="text-capitalize">{{ l.location_type }}</td>
                                <td class="text-right" :class="l.qty <= 0 ? 'text-danger' : ''">{{ l.qty }}</td>
                            </tr>
                            <tr v-if="!detail.locations || !detail.locations.length">
                                <td colspan="3" class="text-muted text-center">No stock at any location yet.</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- batches -->
                <div class="font-weight-bold mb-1">Batches</div>
                <div class="table-responsive mb-3" style="max-height:200px;overflow:auto;">
                    <table class="table table-sm">
                        <thead><tr><th>Batch / Lot</th><th>Date</th><th>Location</th><th class="text-right">Quantity</th></tr></thead>
                        <tbody>
                            <tr v-for="(b, i) in detail.batches" :key="'b'+i">
                                <td>{{ b.reference_no || '-' }}</td>
                                <td>{{ b.purchase_date || '-' }}</td>
                                <td>{{ b.location_name || '-' }}</td>
                                <td class="text-right">{{ b.qty }}</td>
                            </tr>
                            <tr v-if="!detail.batches || !detail.batches.length">
                                <td colspan="4" class="text-muted text-center">No batches recorded.</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- stock history -->
                <div class="font-weight-bold mb-1">Stock history</div>
                <div class="table-responsive mb-3" style="max-height:240px;overflow:auto;">
                    <table class="table table-sm">
                        <thead><tr><th>Date</th><th>Type</th><th>Location</th><th>Batch</th><th class="text-right">Change</th><th class="text-right">Balance</th><th>By</th><th>Note</th></tr></thead>
                        <tbody>
                            <tr v-for="(m, i) in detail.movements" :key="'m'+i">
                                <td>{{ formatDateTime(m.created_at) }}</td>
                                <td class="text-capitalize">{{ m.type }}</td>
                                <td>{{ m.location_name || '-' }}</td>
                                <td>{{ m.batch_number || '-' }}</td>
                                <td class="text-right" :class="m.qty_change < 0 ? 'text-danger' : 'text-success'">{{ m.qty_change > 0 ? '+' + m.qty_change : m.qty_change }}</td>
                                <td class="text-right">{{ m.balance_after }}</td>
                                <td>{{ m.user_name || '-' }}</td>
                                <td>{{ m.note || '-' }}</td>
                            </tr>
                            <tr v-if="!detail.movements || !detail.movements.length">
                                <td colspan="8" class="text-muted text-center">No stock movements recorded yet.</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Add / Deduct panel -->
                <div v-if="showAdjust" class="border rounded p-3 bg-light">
                    <div class="font-weight-bold mb-3">Add / Deduct stock</div>
                    <div class="row">
                        <div class="col-md-6 mb-3">
                            <label>Location <span class="text-danger">*</span></label>
                            <select class="form-control" v-model="form.branch_or_warehouse_id">
                                <option value="">Select location</option>
                                <option v-for="loc in locationList" :key="loc.id" :value="loc.id">
                                    {{ loc.name }}<span v-if="loc.type"> ({{ loc.type }})</span>
                                </option>
                            </select>
                        </div>
                        <div class="col-md-6 mb-3">
                            <label class="d-block">Action <span class="text-danger">*</span></label>
                            <div class="d-flex align-items-center" style="height:38px;">
                                <label class="mr-3 mb-0"><input type="radio" value="add" v-model="form.action" /> Add</label>
                                <label class="mb-0"><input type="radio" value="deduct" v-model="form.action" /> Deduct</label>
                            </div>
                        </div>
                        <div class="col-md-6 mb-3">
                            <label>Quantity <span class="text-danger">*</span></label>
                            <input type="number" min="1" class="form-control" v-model.number="form.qty" placeholder="Enter quantity" />
                        </div>
                        <div class="col-md-6 mb-3">
                            <label>Batch number</label>
                            <input type="text" maxlength="10" class="form-control" v-model="form.batch_number" placeholder="Up to 10 characters" />
                        </div>
                        <div class="col-md-12 mb-3">
                            <label>Note</label>
                            <input type="text" maxlength="500" class="form-control" v-model="form.note" placeholder="Optional note" />
                        </div>
                    </div>
                    <div>
                        <button class="btn btn-primary mr-2" :disabled="saving" @click="submitAdjust">
                            {{ saving ? 'Saving…' : 'Save' }}
                        </button>
                        <button class="btn btn-secondary" @click="showAdjust = false">Cancel</button>
                    </div>
                </div>
            </div>
        </template>

        <template slot="footer" class="justify-content-start">
            <button v-if="!showAdjust" class="btn btn-primary mr-2" @click="openAdjust">Add / Deduct stock</button>
            <app-cancel-button btn-class="btn-secondary" @click="closeModal" />
        </template>
    </app-modal>
</template>

<script>
import HelperMixin from "../../../../common/Mixin/Global/HelperMixin";
import { axiosGet, axiosPost } from "../../../../common/Helper/AxiosHelper";
import { STOCK_DETAIL, STOCK_ADJUST } from "../../../Config/ApiUrl-CP";

export default {
    name: 'app-stock-detail-modal',
    mixins: [HelperMixin],
    props: {
        variantId: { required: true },
    },
    data() {
        return {
            loading: true,
            saving: false,
            showAdjust: false,
            detail: {},
            form: {
                branch_or_warehouse_id: '',
                action: 'add',
                qty: null,
                batch_number: '',
                note: '',
            },
            locationList: [],
        };
    },
    computed: {
        categoryName() {
            const map = { 1: 'Raw Material', 2: 'Ready to use', 3: 'Custom Products', 4: 'Service' };
            const mc = this.detail.variant && this.detail.variant.product ? this.detail.variant.product.main_category : null;
            return map[mc] || '-';
        },
    },
    methods: {
        fetchDetail() {
            this.loading = true;
            axiosGet(`${STOCK_DETAIL}${this.variantId}`).then(res => {
                this.detail = res.data;
            }).catch(() => {
                this.$toastr.e('Could not load stock detail.');
            }).finally(() => {
                this.loading = false;
            });
        },
        formatDateTime(value) {
            if (!value) return '-';
            const d = new Date(String(value).replace(' ', 'T'));
            if (isNaN(d.getTime())) return String(value).substring(0, 16);
            const dd = String(d.getDate()).padStart(2, '0');
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const yyyy = d.getFullYear();
            const hh = String(d.getHours()).padStart(2, '0');
            const mi = String(d.getMinutes()).padStart(2, '0');
            return `${dd}-${mm}-${yyyy} ${hh}:${mi}`;
        },
        fetchLocations() {
            axiosGet('app/selectable-branches-or-warehouses?per_page=100').then(res => {
                const rows = res.data && res.data.data ? res.data.data : (Array.isArray(res.data) ? res.data : []);
                this.locationList = rows.map(r => ({ id: r.id, name: r.name, type: r.type }));
            }).catch(() => {
                this.locationList = [];
            });
        },
        openAdjust() {
            this.form = { branch_or_warehouse_id: '', action: 'add', qty: null, batch_number: '', note: '' };
            this.showAdjust = true;
            if (!this.locationList.length) this.fetchLocations();
        },
        submitAdjust() {
            if (!this.form.branch_or_warehouse_id) {
                this.$toastr.e('Please select a location.');
                return;
            }
            if (!this.form.qty || this.form.qty < 1) {
                this.$toastr.e('Please enter a quantity of 1 or more.');
                return;
            }
            this.saving = true;
            const data = new FormData();
            data.append('variant_id', this.variantId);
            data.append('branch_or_warehouse_id', this.form.branch_or_warehouse_id);
            data.append('action', this.form.action);
            data.append('qty', this.form.qty);
            if (this.form.batch_number) data.append('batch_number', this.form.batch_number);
            if (this.form.note) data.append('note', this.form.note);

            axiosPost(STOCK_ADJUST, data).then(res => {
                this.$toastr.s(res.data.message || 'Stock updated.');
                this.showAdjust = false;
                this.fetchDetail();      // refresh locations/batches/total
                this.$emit('updated');   // refresh the list behind the modal
            }).catch(({ response }) => {
                this.$toastr.e(response && response.data ? response.data.message : 'Could not update stock.');
            }).finally(() => {
                this.saving = false;
            });
        },
        closeModal() {
            $('#app-stock-detail-modal').modal('hide');
            this.$emit('modal-close');
        },
    },
    mounted() {
        $('#app-stock-detail-modal').modal('show');
        this.fetchDetail();
    },
};
</script>
